import { StateSnapshot } from 'react-virtuoso';
import { SelloutTotalsProductColor } from '../../../../../services/modules/product-color-sellout/product-color-sellout.types';
import { CatalogSectionCount } from '../../../catalog.types';
import { saleItemIsProductColor } from '../../../utils/saleItemType.utils';
import { CatalogItemsLimits, ESProductCatalogSearch } from '../../product-catalog-es.types';
import {
  CatalogSelloutMap,
  ComputeAnchorPageParams,
  PageFromSnapshotParams,
  PageSlot,
  ResolveRowCardsParams,
  RowCard,
  RowCards,
} from '../catalogProductsListV2.types';

export const buildSectionItemOffsets = (
  sections: CatalogSectionCount[],
): Record<string, number> => {
  const offsets: Record<string, number> = {};
  let accumulatedItems = 0;

  for (const section of sections) {
    offsets[section.catalogSectionId] = accumulatedItems;
    accumulatedItems += section.itemsCount;
  }

  return offsets;
};

export const findSectionByRowIndex = (
  rowIndex: number,
  sectionLimits: CatalogItemsLimits[],
): CatalogItemsLimits => {
  if (!sectionLimits.length) {
    throw new Error('findSectionByRowIndex requires a non-empty sectionLimits array');
  }

  const matchedSection = sectionLimits.find(
    (limit) => rowIndex >= limit.startRowIndex && rowIndex <= limit.endRowIndex,
  );

  if (matchedSection) {
    return matchedSection;
  }

  const firstSection = sectionLimits[0];
  if (rowIndex < firstSection.startRowIndex) {
    return firstSection;
  }

  return sectionLimits[sectionLimits.length - 1];
};

export const computeAnchorPage = ({
  range,
  maxItemsPerRow,
  pageSize,
  sectionLimits,
  sectionItemOffsets,
}: ComputeAnchorPageParams): number => {
  if (!sectionLimits.length) {
    return 0;
  }

  const section = findSectionByRowIndex(range.startIndex, sectionLimits);
  const localRow = range.startIndex - section.startRowIndex;
  const globalItemIndex = sectionItemOffsets[section.sectionId] + localRow * maxItemsPerRow;

  return Math.floor(globalItemIndex / pageSize);
};

const deriveTopRowIndex = (snapshot: StateSnapshot): number | undefined => {
  const { ranges, scrollTop } = snapshot;

  if (!ranges.length) {
    return undefined;
  }

  if (scrollTop <= 0) {
    return ranges[0].startIndex;
  }

  let accumulatedHeight = 0;
  for (const range of ranges) {
    const rowsInRange = range.endIndex - range.startIndex + 1;
    const rangeHeight = rowsInRange * range.size;

    if (scrollTop < accumulatedHeight + rangeHeight) {
      const rowsScrolled =
        range.size > 0 ? Math.floor((scrollTop - accumulatedHeight) / range.size) : 0;
      return range.startIndex + rowsScrolled;
    }

    accumulatedHeight += rangeHeight;
  }

  return ranges[ranges.length - 1].endIndex;
};

export const pageFromSnapshot = ({
  snapshot,
  maxItemsPerRow,
  pageSize,
  sectionLimits,
  sectionItemOffsets,
}: PageFromSnapshotParams): number | undefined => {
  if (!snapshot || !sectionLimits.length) {
    return undefined;
  }

  const topRowIndex = deriveTopRowIndex(snapshot);
  if (topRowIndex === undefined) {
    return undefined;
  }

  const section = findSectionByRowIndex(topRowIndex, sectionLimits);
  const localRow = topRowIndex - section.startRowIndex;
  const globalItemIndex = sectionItemOffsets[section.sectionId] + localRow * maxItemsPerRow;
  const derivedPage = Math.floor(globalItemIndex / pageSize);

  const totalItems = sectionLimits.reduce((sum, limit) => sum + limit.itemsCount, 0);
  const lastPage = Math.max(0, Math.ceil(totalItems / pageSize) - 1);

  return Math.min(Math.max(derivedPage, 0), lastPage);
};

const resolveSlotStateCard = (slot?: PageSlot): RowCard | undefined => {
  if (!slot || slot.status === 'loading') {
    return { kind: 'skeleton' };
  }

  return undefined;
};

const resolveSelloutMetrics = (
  esProduct: ESProductCatalogSearch,
  sellout: CatalogSelloutMap,
): SelloutTotalsProductColor | undefined => {
  if (!saleItemIsProductColor(esProduct.item)) {
    return undefined;
  }

  return sellout.get(esProduct.item.productColorId);
};

export const resolveRowCards = ({
  rowIndex,
  maxItemsPerRow,
  pageSize,
  sectionLimits,
  sectionItemOffsets,
  pagesById,
  sellout,
  access,
}: ResolveRowCardsParams): RowCards => {
  const section = findSectionByRowIndex(rowIndex, sectionLimits);
  const localRow = rowIndex - section.startRowIndex;
  const sectionOffset = sectionItemOffsets[section.sectionId];

  const resolveCard = (col: number): RowCard => {
    const localItemIndex = localRow * maxItemsPerRow + col;
    if (localItemIndex >= section.itemsCount) {
      return { kind: 'empty' };
    }

    const globalItemIndex = sectionOffset + localItemIndex;
    const slot = pagesById.get(Math.floor(globalItemIndex / pageSize));

    const slotStateCard = resolveSlotStateCard(slot);
    if (slotStateCard) {
      return slotStateCard;
    }

    const esProduct = slot?.data?.[globalItemIndex % pageSize];
    if (!esProduct) {
      return { kind: 'empty' };
    }

    return {
      kind: 'item',
      esProduct,
      selloutMetrics: resolveSelloutMetrics(esProduct, sellout),
      canAddToOrder: access.canAddToOrder(esProduct),
      isSuggested: access.isSuggested(esProduct),
    };
  };

  const cards = Array.from({ length: maxItemsPerRow }, (_, col) => resolveCard(col));

  return { sectionId: section.sectionId, cards };
};
